import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Alert, Form, Row, Col, Button, InputGroup, Modal, Toast, ToastContainer
} from 'react-bootstrap';
import { FaCheck, FaTimes, FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from "../../contexts/AuthContext";


const DPDPRetentionTab = ({userData, onScoreUpdate,logToAccessLogs }) => {
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [upcomingDeletions, setUpcomingDeletions] = useState([]);
  const [showErasureModal, setShowErasureModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [erasureLog, setErasureLog] = useState('');
  const { profile } = useAuth();
     const organizationId = profile?.orgId;
  
  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success' // success, danger, warning, info
  });
  
  const [newPolicy, setNewPolicy] = useState({
    category: '',
    periodValue: '',
    periodUnit: 'Years',
    legal: '',
    autoDelete: false,
    // NEW MANDATORY FIELDS
    purposeCompletionTrigger: '',
    retentionJustification: '',
    hasLegalException: false,
    legalExceptionReason: '',
    autoErasureEnabled: false
  });


  const getUserRegion = () => sessionStorage.getItem('userRegion') || 'GB';
  const headers = {
    Region: getUserRegion() || 'GB',
    organizationId: organizationId || ''
  };


  // Toast helper function
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
  };


  const logActivity = async (message, badge = "info") => {
    try {
      await axios.post(
            "https://ugat9qq1mj.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/activities/create",
            {
              message,
              badge,
              timestamp: new Date().toISOString()
            },
            {
              headers: {
                Region: getUserRegion(),
                organizationId: organizationId
              }
         });
        }catch (err) {
      console.warn("Failed to log activity:", err.message);
    }
  };


  useEffect(() => {
    fetchPolicies();
  }, []);


  const fetchPolicies = async () => {
    try {
      const policyRes = await axios.get('https://82bcadcihg.execute-api.ap-southeast-2.amazonaws.com/prod/api/retention-policy/list', { headers });
      setRetentionPolicies(policyRes.data);


      const deletionRes = await axios.get('https://bz2xvniw88.execute-api.ap-southeast-2.amazonaws.com/prod/api/retention-policy/scheduled-deletions', { headers });
      setUpcomingDeletions(deletionRes.data);


      // Enhanced scoring for DPDP compliance
      const MIN_CATEGORIES = [
        'Employee Records',
        'Customer Data',
        'Financial Transactions',
        'Website Analytics'
      ];
      const MAX_RETENTION_YEARS = 7;


      const totalPolicies = policyRes.data.length;
      const autoDeleteCount = policyRes.data.filter(p => p.autoDelete).length;
      const fullyConfigured = policyRes.data.filter(
        p => p.category && 
           p.legal && 
           p.periodValue > 0 && 
           p.purposeCompletionTrigger && 
           p.retentionJustification
      ).length;
      const overdueRetention = policyRes.data.filter(
        p => p.periodUnit === 'Years' && p.periodValue > MAX_RETENTION_YEARS && !p.hasLegalException
      ).length;
      const missingCategories = MIN_CATEGORIES.filter(
        cat => !policyRes.data.some(p => p.category === cat)
      ).length;


      let score = 0;
      if (totalPolicies === 0) {
        score = 0;
      } else {
        score = 100;
        score -= missingCategories * 20;
        score -= overdueRetention * 15;
        score -= (totalPolicies - fullyConfigured) * 10;
        score = Math.max(0, Math.min(100, score));
      }


      onScoreUpdate(Math.round(score));


    } catch (error) {
      console.error('Error fetching policies or deletions', error);
      showToast('Failed to fetch retention policies. Please try again.', 'danger');
    }
  };


  const handleNewPolicyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPolicy((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };


  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    const { 
      category, 
      periodValue, 
      periodUnit, 
      legal, 
      autoDelete,
      purposeCompletionTrigger,
      retentionJustification,
      hasLegalException,
      legalExceptionReason,
      autoErasureEnabled
    } = newPolicy;


    const numVal = Number(periodValue);
    
    // Enhanced validation
    if (!category || !numVal || numVal <= 0 || !legal || !purposeCompletionTrigger || !retentionJustification) {
      showToast('Please fill all mandatory fields including purpose completion trigger and retention justification', 'warning');
      return;
    }


    if (hasLegalException && !legalExceptionReason) {
      showToast('Please provide a reason for legal retention exception', 'warning');
      return;
    }


    const payload = {
      category: category.trim(),
      periodValue: numVal,
      periodUnit,
      legal,
      autoDelete,
      purposeCompletionTrigger,
      retentionJustification,
      hasLegalException,
      legalExceptionReason: hasLegalException ? legalExceptionReason : null,
      autoErasureEnabled,
      status: 'Active'
    };


    try {
      await axios.post('https://6rrdq9qjld.execute-api.ap-southeast-2.amazonaws.com/prod/api/retention-policy/create', payload, { headers });
      await logActivity(`Created retention policy for ${payload.category}`, "success");
      logToAccessLogs?.({
  action:      'Retention Policy Created',
  severity:    'success',
  description: `Created retention policy for ${payload.category} â€” ${payload.periodValue} ${payload.periodUnit}`,});
      await fetchPolicies();
      
      showToast(`âœ“ Retention policy for "${payload.category}" created successfully!`, 'success');
      
      // Reset form
      setNewPolicy({
        category: '',
        periodValue: '',
        periodUnit: 'Years',
        legal: '',
        autoDelete: false,
        purposeCompletionTrigger: '',
        retentionJustification: '',
        hasLegalException: false,
        legalExceptionReason: '',
        autoErasureEnabled: false
      });
    } catch (error) {
      console.error('Error saving policy:', error);
      showToast('Failed to create retention policy. Please try again.', 'danger');
    }
  };


  const handleConfirmErasure = async () => {
    if (!erasureLog.trim()) {
      showToast('Please provide erasure confirmation details', 'warning');
      return;
    }


    try {
      await axios.post('https://xfrlqn78n7.execute-api.ap-southeast-2.amazonaws.com/prod/api/retention-policy/confirm-erasure', {
        policyId: selectedPolicy.policyId,
        erasureLog: erasureLog,
        confirmedBy: headers.organizationId,
        confirmedAt: new Date().toISOString()
      }, { headers });


      await logActivity(`Confirmed data erasure for ${selectedPolicy.category}`, "warning");
      logToAccessLogs?.({
  action:      'Data Erasure Confirmed',
  severity:    'critical',
  description: `Confirmed data erasure for ${selectedPolicy.category} â€” Policy ID: ${selectedPolicy.policyId}`,
    });
      showToast(`âœ“ Data erasure confirmed for "${selectedPolicy.category}"`, 'success');
      
      setShowErasureModal(false);
      setSelectedPolicy(null);
      setErasureLog('');
      fetchPolicies();
    } catch (error) {
      console.error('Error confirming erasure:', error);
      showToast('Failed to confirm data erasure. Please try again.', 'danger');
    }
  };


 const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  


  return (
    <>
      {/* Toast Container - positioned at top-right */}
      <ToastContainer position="top-end" className="p-3" style={{ position: 'fixed', zIndex: 9999 }}>
        <Toast 
          show={toast.show} 
          onClose={() => setToast({ ...toast, show: false })} 
          delay={5000} 
          autohide
          bg={toast.variant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toast.variant === 'success' && 'âœ“ Success'}
              {toast.variant === 'danger' && 'âœ• Error'}
              {toast.variant === 'warning' && 'âš  Warning'}
              {toast.variant === 'info' && 'â„¹ Information'}
            </strong>
            <small>just now</small>
          </Toast.Header>
          <Toast.Body className={toast.variant === 'success' || toast.variant === 'danger' ? 'text-white' : ''}>
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="border rounded">
  <div
    className="d-flex justify-content-between align-items-center p-3" 
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
    }
  >
    <h6>Active Retention Policies (DPDP Act 2023)</h6>
    <span>{activeAccordionKey === "0" ? "â–²" : "â–¼"}</span>
  </div>

  {activeAccordionKey === "0" && (
    <div className="p-3 border-top">
            <Table striped bordered responsive>
              <thead>
                <tr>
                  <th>Data Category</th>
                  <th>Retention Period</th>
                  <th>Purpose Trigger</th>
                  <th>Justification</th>
                  <th>Legal Exception</th>
                  <th>Auto-Erasure</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {retentionPolicies.map((policy) => (
                  <tr key={policy.id}>
                    <td>{policy.category}</td>
                    <td>{policy.periodValue} {policy.periodUnit}</td>
                    <td>
                      <small className="text-muted">
                        {policy.purposeCompletionTrigger || 'â€”'}
                      </small>
                    </td>
                    <td>
                      <small className="text-muted">
                        {policy.retentionJustification || 'â€”'}
                      </small>
                    </td>
                    <td>
                      {policy.hasLegalException ? (
                        <div>
                          <Badge bg="warning" className="mb-1">
                            <FaExclamationTriangle /> Exception
                          </Badge>
                          <br />
                          <small>{policy.legalExceptionReason}</small>
                        </div>
                      ) : (
                        <FaTimes className="text-muted" />
                      )}
                    </td>
                    <td className="text-center">
                      {policy.autoErasureEnabled ? (
                        <FaCheck className="text-success" />
                      ) : (
                        <FaTimes className="text-danger" />
                      )}
                    </td>
                    <td>
                      <Badge bg={policy.status === 'Active' ? 'success' : 'secondary'}>
                        {policy.status}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => {
                          setSelectedPolicy(policy);
                          setShowErasureModal(true);
                        }}
                      >
                        <FaTrash /> Log Erasure
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
         </div>
  )}


        <div
    className="d-flex justify-content-between align-items-center p-3" 
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "1" ? null : "1")
    }
  >
    <h6>Scheduled Deletions</h6>
    <span>{activeAccordionKey === "1" ? "â–²" : "â–¼"}</span>
  </div>

  {activeAccordionKey === "1" && (
    <div className="p-3 border-top">
            <Alert variant="info">
              <strong>Upcoming Deletions:</strong>
              {upcomingDeletions.length > 0 ? (
                <ul className="mb-0 mt-2">
                  {upcomingDeletions.map((item, index) => (
                    <li key={index}>
                      <strong>{item.category}</strong> â†’ Deletion on <em>{item.nextDeletionDate}</em>
                      {item.autoErasureEnabled && <Badge bg="success" className="ms-2">Auto-Erasure</Badge>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mb-0 mt-2">No scheduled deletions at this time.</p>
              )}
            </Alert>
          </div>
  )}


        <div
    className="d-flex justify-content-between align-items-center p-3" 
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "2" ? null : "2")
    }
  >
    <h6>Create New Retention Policy</h6>
    <span>{activeAccordionKey === "2" ? "â–²" : "â–¼"}</span>
  </div>

  {activeAccordionKey === "2" && (
    <div className="p-3 border-top">
            <Form onSubmit={handlePolicySubmit}>
              <Alert variant="warning" className="mb-3">
                <strong>DPDP Act 2023 Requirements:</strong> All retention policies must include purpose completion triggers, justification, and auto-erasure mechanisms.
              </Alert>


              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Data Category <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      value={newPolicy.category}
                      onChange={handleNewPolicyChange}
                      placeholder="e.g., Employee Records"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Retention Period <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="periodValue"
                      value={newPolicy.periodValue}
                      onChange={handleNewPolicyChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>&nbsp;</Form.Label>
                    <Form.Select
                      name="periodUnit"
                      value={newPolicy.periodUnit}
                      onChange={handleNewPolicyChange}
                    >
                      <option>Days</option>
                      <option>Months</option>
                      <option>Years</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>


              {/* NEW MANDATORY FIELDS */}
              <Form.Group className="mb-3">
                <Form.Label>Purpose Completion Trigger <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="purposeCompletionTrigger"
                  value={newPolicy.purposeCompletionTrigger}
                  onChange={handleNewPolicyChange}
                  placeholder="e.g., Contract termination + 90 days, Account closure, Project completion"
                  required
                />
                <Form.Text className="text-muted">
                  Define when the processing purpose is considered complete
                </Form.Text>
              </Form.Group>


              <Form.Group className="mb-3">
                <Form.Label>Retention Justification <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  name="retentionJustification"
                  rows={3}
                  value={newPolicy.retentionJustification}
                  onChange={handleNewPolicyChange}
                  placeholder="Explain why this retention period is necessary (e.g., legal obligations, audit requirements)"
                  required
                />
              </Form.Group>


              <Form.Group className="mb-3">
                <Form.Label>Legal Basis</Form.Label>
                <Form.Control
                  as="textarea"
                  name="legal"
                  rows={2}
                  value={newPolicy.legal}
                  onChange={handleNewPolicyChange}
                  placeholder="e.g., Companies Act 2013, Income Tax Act, DPDP Act 2023"
                />
              </Form.Group>


              {/* Legal Exception Handling */}
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="hasLegalException"
                  label="This data requires extended retention due to legal obligations"
                  checked={newPolicy.hasLegalException}
                  onChange={handleNewPolicyChange}
                />
              </Form.Group>


              {newPolicy.hasLegalException && (
                <Form.Group className="mb-3">
                  <Form.Label>Legal Exception Reason <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    name="legalExceptionReason"
                    rows={2}
                    value={newPolicy.legalExceptionReason}
                    onChange={handleNewPolicyChange}
                    placeholder="Specify the legal requirement (e.g., Tax records must be retained for 7 years under Income Tax Act)"
                  />
                </Form.Group>
              )}


              {/* Auto-Erasure Controls */}
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="autoErasureEnabled"
                  label="Enable automatic erasure when retention period expires"
                  checked={newPolicy.autoErasureEnabled}
                  onChange={handleNewPolicyChange}
                />
                <Form.Text className="text-muted">
                  Recommended: Data will be automatically deleted when purpose is fulfilled and retention period expires
                </Form.Text>
              </Form.Group>


              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="autoDelete"
                  label="Legacy auto-delete flag (for backward compatibility)"
                  checked={newPolicy.autoDelete}
                  onChange={handleNewPolicyChange}
                />
              </Form.Group>


              <Button type="submit" variant="primary">Create Retention Policy</Button>
            </Form>
          </div>
  )}

</div>

      {/* Erasure Confirmation Modal */}
      <Modal show={showErasureModal} onHide={() => setShowErasureModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Data Erasure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPolicy && (
            <>
              <Alert variant="warning">
                <strong>Category:</strong> {selectedPolicy.category}<br />
                <strong>Retention Period:</strong> {selectedPolicy.periodValue} {selectedPolicy.periodUnit}
              </Alert>
              
              <Form.Group className="mb-3">
                <Form.Label>Erasure Confirmation Log <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={erasureLog}
                  onChange={(e) => setErasureLog(e.target.value)}
                  placeholder="Document the erasure process:
- Date and time of erasure
- Systems/databases affected
- Verification method used
- Confirmation that data is irrecoverable"
                />
              </Form.Group>


              <Alert variant="info">
                <small>
                  <strong>DPDP Compliance:</strong> This log will be stored as proof of data erasure as required under the Digital Personal Data Protection Act 2023.
                </small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErasureModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmErasure}>
            <FaCheck /> Confirm Erasure
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};


export default DPDPRetentionTab;
